import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { tutorial } from "../api/client";
import { useAuth } from "../auth/AuthContext";

/** The new-user tour. The server makes sample receipts on start (one flagged by
 *  a ready-made AI review) and deletes them when the tour ends or is skipped. */

interface TourData {
  receiptId: string;
  vendorName: string;
}

interface Step {
  path: (d: TourData) => string;
  /** CSS selector to spotlight; none = a centred card. */
  target?: string;
  title: string;
  body: ReactNode;
}

const STEPS: Step[] = [
  {
    path: () => "/app/dashboard",
    title: "Welcome to Set & Ledger",
    body: (
      <>
        This two-minute tour shows how receipts get in, where to find them and how to review them. We've added a
        sample receipt from a made-up coffee shop so you can try everything. It's deleted when the tour ends.
      </>
    ),
  },
  {
    path: () => "/app/connections",
    target: '[data-tour="connect"]',
    title: "1. Connect your mailbox",
    body: (
      <>
        Connect Gmail, Outlook or any IMAP inbox. Access is read-only: the AI files every receipt, bill and payment it
        finds, including PDFs. You can do this right after the tour.
      </>
    ),
  },
  {
    path: (d) => `/app/receipts?q=${encodeURIComponent(d.vendorName)}`,
    target: '[data-tour="sample-row"]',
    title: "2. Your ledger",
    body: (
      <>
        Everything the AI finds lands in the ledger, newest first: <strong>receipts</strong> (paid),{" "}
        <strong>bills</strong> (owed) and <strong>income</strong>. This row is the sample. Next, we'll open it.
      </>
    ),
  },
  {
    path: (d) => `/app/receipts/${d.receiptId}`,
    target: '[data-tour="receipt-fields"]',
    title: "3. What the AI read",
    body: (
      <>
        Vendor, amount, tax, dates, invoice number and card. If something's wrong, <strong>Edit</strong> fixes it.
        Duplicates and payments of earlier bills are linked here too.
      </>
    ),
  },
  {
    path: (d) => `/app/receipts/${d.receiptId}`,
    target: '[data-tour="receipt-source"]',
    title: "The original email",
    body: <>The email and any attachment it came with, right beside the numbers, so checking takes a glance.</>,
  },
  {
    path: (d) => `/app/review?g=${encodeURIComponent(`vendor:${d.vendorName}`)}`,
    target: '[data-tour="finding"]',
    title: "4. Bulk Review, one vendor at a time",
    body: (
      <>
        Bulk Review lives under <strong>Ledger</strong> in the menu. The AI compares each vendor's receipts and flags
        anything that needs you. Here the sample was charged in USD while this vendor bills in CAD. Try the{" "}
        <strong>Set currency to CAD</strong> button.
      </>
    ),
  },
  {
    path: (d) => `/app/review?g=${encodeURIComponent(`vendor:${d.vendorName}`)}`,
    target: '[data-tour="bulk-review"]',
    title: "Mark them reviewed together",
    body: (
      <>
        Tick the items you've checked (the box in the header selects them all) and mark them reviewed in one go. Once
        every item is done, the next vendor opens. A fix you'd want every time (type, currency or card) can be saved
        with <strong>Always for this vendor</strong>.
      </>
    ),
  },
  {
    path: () => "/app/dashboard",
    title: "You're all set",
    body: <>We've removed the sample receipts. Connect a mailbox and your real receipts will start arriving.</>,
  },
];

// Progress is kept per user, so another account on this browser never resumes it.
const storageKey = (userId: string) => `sl-tour:${userId}`;

function load(userId: string): { step: number; data: TourData } | null {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function save(userId: string, value: { step: number; data: TourData } | null) {
  try {
    localStorage.removeItem("sl-tour"); // the old shared key
    if (value) localStorage.setItem(storageKey(userId), JSON.stringify(value));
    else localStorage.removeItem(storageKey(userId));
  } catch {
    /* private mode: the tour just won't survive a reload */
  }
}

interface TourContextValue {
  start: () => Promise<void>;
  active: boolean;
}

const TourContext = createContext<TourContextValue>({ start: async () => undefined, active: false });

export const useTour = () => useContext(TourContext);

export function TourProvider({ children }: { children: ReactNode }) {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState<{ step: number; data: TourData } | null>(null);
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  // Once the tour has started, ended or been resumed in this session, it never auto-starts again.
  const settled = useRef(false);
  const finished = useRef(false);
  const userId = user?.id ?? null;

  useEffect(() => {
    if (!userId) return;
    const saved = load(userId);
    if (saved) settled.current = true;
    setState(saved);
    setLoadedFor(userId);
  }, [userId]);

  const set = useCallback((next: { step: number; data: TourData } | null) => {
    if (userId) save(userId, next);
    setState(next);
  }, [userId]);

  const start = useCallback(async () => {
    settled.current = true;
    finished.current = false;
    const res = await tutorial.start();
    set({ step: 0, data: { receiptId: res.receipt_id, vendorName: res.vendor_name } });
  }, [set]);

  // New users get the tour on their first visit.
  useEffect(() => {
    if (!user || loadedFor !== user.id || user.tutorial_done_at || state || settled.current) return;
    start().catch(() => undefined);
  }, [user, loadedFor, state, start]);

  const finish = useCallback(async () => {
    if (finished.current) return;
    finished.current = true;
    try {
      await tutorial.finish();
    } finally {
      await refreshUser();
    }
  }, [refreshUser]);

  const close = useCallback(
    async (then?: string) => {
      settled.current = true;
      set(null);
      if (then) navigate(then);
      await finish().catch(() => undefined);
    },
    [finish, navigate, set],
  );

  const go = useCallback(
    (step: number) => {
      if (!state) return;
      set({ ...state, step });
      // The last card says the samples are gone, so remove them as it opens.
      if (step === STEPS.length - 1) finish().catch(() => undefined);
    },
    [state, set, finish],
  );

  return (
    <TourContext.Provider value={{ start, active: !!state }}>
      {children}
      {state && <TourCard step={state.step} data={state.data} go={go} close={close} />}
    </TourContext.Provider>
  );
}

function useTargetRect(selector: string | undefined, key: string) {
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [missing, setMissing] = useState(false);
  useLayoutEffect(() => {
    setRect(null);
    setMissing(false);
    if (!selector) return;
    let scrolled = false;
    const started = Date.now();
    const measure = () => {
      const el = document.querySelector(selector);
      if (!el) {
        if (Date.now() - started > 4000) setMissing(true);
        return;
      }
      setMissing(false); // a late target still gets its spotlight
      if (!scrolled) {
        el.scrollIntoView({ block: "center", behavior: "smooth" });
        scrolled = true;
      }
      const r = el.getBoundingClientRect();
      setRect((prev) =>
        prev && prev.top === r.top && prev.left === r.left && prev.width === r.width && prev.height === r.height ? prev : r,
      );
    };
    measure();
    // The page loads its data after navigating, and things move as it does.
    const timer = window.setInterval(measure, 200);
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [selector, key]);
  return { rect, missing };
}

const GUTTER = 16;
const PAD = 6;

function TourCard({ step, data, go, close }: {
  step: number;
  data: TourData;
  go: (step: number) => void;
  close: (then?: string) => void;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const def = STEPS[step] ?? STEPS[0];
  const path = def.path(data);
  const last = step === STEPS.length - 1;
  const cardRef = useRef<HTMLDivElement>(null);
  const [cardSize, setCardSize] = useState({ w: 360, h: 200 });

  // Back on the step's page on every step (the user may have wandered off).
  useEffect(() => {
    if (location.pathname + location.search !== path) navigate(path);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, step]);

  const { rect, missing } = useTargetRect(def.target, `${step}`);
  useLayoutEffect(() => {
    if (cardRef.current) setCardSize({ w: cardRef.current.offsetWidth, h: cardRef.current.offsetHeight });
  }, [step, rect]);

  const centred = !def.target || missing || !rect;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  // Keep the highlight on screen when the target is wider than it (a scrolling table).
  const spot = rect && {
    top: rect.top - PAD,
    left: Math.max(4, rect.left - PAD),
    width: Math.min(vw - 4, rect.right + PAD) - Math.max(4, rect.left - PAD),
    height: rect.height + PAD * 2,
  };
  let style: CSSProperties;
  if (centred) {
    style = { left: Math.max(GUTTER, (vw - cardSize.w) / 2), top: Math.max(GUTTER, (vh - cardSize.h) / 2) };
  } else {
    const below = rect.bottom + PAD + 12;
    const above = rect.top - PAD - 12 - cardSize.h;
    const top = below + cardSize.h <= vh - GUTTER || above < GUTTER ? Math.min(below, vh - GUTTER - cardSize.h) : above;
    const left = Math.min(Math.max(GUTTER, rect.left), vw - GUTTER - cardSize.w);
    style = { left, top: Math.max(GUTTER, top) };
  }

  return (
    <div className="tour-layer" role="dialog" aria-modal="false" aria-labelledby="tour-title">
      {centred ? (
        <div className="tour-backdrop" />
      ) : (
        <div className="tour-spotlight" style={spot ?? undefined} />
      )}
      <div className="tour-card" ref={cardRef} style={style}>
        <button className="tour-close" aria-label="End the tour" onClick={() => close()}>
          <X size={16} />
        </button>
        <div className="tour-step">
          {step + 1} of {STEPS.length}
        </div>
        <h3 id="tour-title">{def.title}</h3>
        <p>{def.body}</p>
        <div className="tour-dots" aria-hidden="true">
          {STEPS.map((_, i) => (
            <span key={i} className={i === step ? "on" : ""} />
          ))}
        </div>
        <div className="tour-actions">
          {last ? (
            <>
              <button className="btn btn-ghost btn-sm" onClick={() => close()}>
                Done
              </button>
              <button className="btn btn-primary btn-sm" onClick={() => close("/app/connections")}>
                Connect a mailbox
              </button>
            </>
          ) : (
            <>
              {step === 0 ? (
                <button className="btn btn-ghost btn-sm" onClick={() => close()}>
                  Skip the tour
                </button>
              ) : (
                <button className="btn btn-ghost btn-sm" onClick={() => go(step - 1)}>
                  Back
                </button>
              )}
              <button className="btn btn-primary btn-sm" onClick={() => go(step + 1)}>
                {step === 0 ? "Start the tour" : "Next"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
